import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal, Search as SearchIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const PAGE_SIZE = 12;

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const search = params.get("search") || "";

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [localSearch, setLocalSearch] = useState(search);
  const [sort, setSort] = useState("featured");

  const skipRef = useRef(0);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  const fetchProducts = useCallback(
    async (reset) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      if (reset) {
        skipRef.current = 0;
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const q = { skip: reset ? 0 : skipRef.current, limit: PAGE_SIZE, sort };
      if (category) q.category = category;
      if (search) q.search = search;
      try {
        const r = await api.get("/products", { params: q });
        const batch = r.data;
        setProducts((prev) => (reset ? batch : [...prev, ...batch]));
        skipRef.current = (reset ? 0 : skipRef.current) + batch.length;
        setHasMore(batch.length === PAGE_SIZE);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        fetchingRef.current = false;
      }
    },
    [category, search, sort]
  );

  useEffect(() => {
    fetchProducts(true);
  }, [fetchProducts]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) {
          fetchProducts(false);
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchProducts, loading, products.length]);

  const setCategory = (name) => {
    const next = new URLSearchParams(params);
    if (name) next.set("category", name);
    else next.delete("category");
    next.delete("search");
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (localSearch.trim()) {
      next.set("search", localSearch.trim());
      next.delete("category"); // global search across the whole catalog
    } else {
      next.delete("search");
    }
    setParams(next);
  };

  return (
    <div data-testid="shop-page" className="mx-auto max-w-[1440px] px-3 pb-12 pt-3 sm:px-5 lg:px-8">
      {/* Header banner */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-[2rem] bg-forest px-6 py-10 text-white sm:px-10 lg:rounded-[2.5rem] lg:px-14 lg:py-14"
      >
        <div className="pattern-dots absolute inset-0" />
        <img src="/brand/logo-mark.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-10 right-4 h-56 w-auto opacity-[0.1] lg:right-16 lg:h-72" />
        <div className="relative">
          <span className="label-eyebrow text-ochre">The Mart</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {category || (search ? `Results for “${search}”` : "All products")}
          </h1>
          <p className="mt-3 text-sand/75" data-testid="shop-result-count">
            {products.length} item{products.length !== 1 ? "s" : ""}{hasMore ? "+" : ""} available
          </p>

          {/* Search + sort */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <form onSubmit={submitSearch} className="flex flex-1 items-center rounded-full bg-white py-1.5 pl-5 pr-1.5 sm:max-w-md">
              <SearchIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                data-testid="shop-search-input"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="Search all products…"
                className="w-full bg-transparent px-3 py-2 text-sm text-ink outline-none placeholder:text-muted-foreground"
              />
              <button type="submit" className="shrink-0 rounded-full bg-clay px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#A3161F]">
                Search
              </button>
            </form>
            <label className="flex items-center gap-2 text-sm">
              <SlidersHorizontal className="h-4 w-4 text-sand/70" />
              <span className="sr-only">Sort products</span>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-full border border-white/20 bg-white/10 px-4 py-3 font-semibold text-white outline-none backdrop-blur [&>option]:text-ink"
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="name">Name A–Z</option>
              </select>
            </label>
          </div>
        </div>
      </motion.div>

      {/* Category chips */}
      <div className="no-scrollbar -mx-3 mt-6 flex gap-2 overflow-x-auto px-3 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
        <button
          onClick={() => setCategory("")}
          data-testid="filter-all"
          className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
            !category ? "border-forest bg-forest text-white" : "border-border bg-white text-forest hover:border-forest/40"
          }`}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            onClick={() => setCategory(c.name)}
            data-testid={`filter-${c.slug}`}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
              category === c.name ? "border-forest bg-forest text-white" : "border-border bg-white text-forest hover:border-forest/40"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-[1.75rem] bg-muted" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="mt-16 rounded-[2rem] border border-dashed border-border bg-white px-6 py-16 text-center" data-testid="shop-empty">
          <img src="/brand/logo-mark.png" alt="" aria-hidden="true" className="mx-auto h-20 w-auto opacity-80" />
          <p className="mt-4 font-display text-3xl font-semibold text-forest">Nothing here yet</p>
          <p className="mt-2 text-muted-foreground">Try another category or search term.</p>
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i % PAGE_SIZE} />
            ))}
          </div>
          <div ref={sentinelRef} className="h-12" />
          {loadingMore && (
            <div className="mt-4 flex justify-center" data-testid="shop-loading-more">
              <Loader2 className="h-6 w-6 animate-spin text-clay" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
